import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { TrendingUp, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const CLOSED_STAGES = ["Term Sheet", "Definitive Docs", "Closed"];

/**
 * LogAsInvestmentButton
 * ---------------------
 * One-click promotion of a Closed / Term Sheet / Definitive Docs deal room
 * into a manual portfolio entry. Renders nothing if the deal room isn't in
 * one of those stages.
 *
 * Props:
 *   room         — deal room object with deal_room_id, stage, committed_amount_inr
 *   onLogged     — callback fired after successful conversion
 *   variant      — "primary" | "compact" (default primary)
 */
export default function LogAsInvestmentButton({ room, onLogged, variant = "primary" }) {
  const [open, setOpen] = useState(false);

  if (!CLOSED_STAGES.includes(room?.stage)) return null;

  const btnClass = variant === "compact"
    ? "inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white font-heading transition-colors"
    : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-heading transition-colors";

  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); setOpen(true); }}
        className={btnClass}
        data-testid={`log-investment-btn-${room.deal_room_id}`}
        title="Log this deal as a portfolio investment"
      >
        <TrendingUp size={variant === "compact" ? 10 : 12} /> Log as Investment
      </button>
      {open && (
        <LogFromDealRoomModal
          room={room}
          onClose={() => setOpen(false)}
          onLogged={() => { setOpen(false); onLogged?.(); }}
        />
      )}
    </>
  );
}

function LogFromDealRoomModal({ room, onClose, onLogged }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    invested_amount: room.committed_amount_inr || "",
    equity_pct: "",
    valuation_at_entry: "",
    notes: `From deal room ${room.deal_room_id}`,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.invested_amount) { toast.error("Amount is required"); return; }
    setSaving(true);
    try {
      await api.post(`/investor/portfolio/from-deal-room/${room.deal_room_id}`, {
        invested_amount: Number(form.invested_amount),
        equity_pct: form.equity_pct ? Number(form.equity_pct) : null,
        valuation_at_entry: form.valuation_at_entry ? Number(form.valuation_at_entry) : null,
        notes: form.notes || null,
      });
      toast.success("Investment logged to your portfolio.");
      onLogged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="log-investment-modal">
      <div className="max-w-md w-full bg-white rounded-xl shadow-2xl border border-navy/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
          <div className="font-heading text-ink flex items-center gap-2">
            <TrendingUp size={16} className="text-emerald-600" /> Log this deal
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="log-investment-modal-close">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="text-xs text-ink-muted bg-emerald-50/50 border border-emerald-200 rounded p-2">
            Deal room stage <span className="font-heading text-emerald-800">{room.stage}</span>. Confirm the numbers below —
            we{"\u2019"}ll create a portfolio entry so mark-to-market tracking kicks in.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldLI label="Amount (₹)" type="number" value={form.invested_amount} onChange={(e) => setForm((f) => ({ ...f, invested_amount: e.target.value }))} testId="log-investment-amount" required />
            <FieldLI label="Equity (%)" type="number" step="0.01" value={form.equity_pct} onChange={(e) => setForm((f) => ({ ...f, equity_pct: e.target.value }))} placeholder="e.g. 5" testId="log-investment-equity" />
            <div className="col-span-2">
              <FieldLI label="Entry Valuation (₹)" type="number" value={form.valuation_at_entry} onChange={(e) => setForm((f) => ({ ...f, valuation_at_entry: e.target.value }))} placeholder="Optional — for MtM tracking" testId="log-investment-entry-val" />
            </div>
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">Notes</div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm resize-none"
              data-testid="log-investment-notes"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-navy/15 text-ink text-sm hover:bg-navy/[0.04]" data-testid="log-investment-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-heading transition-colors" data-testid="log-investment-submit">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Log Investment
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function FieldLI({ label, value, onChange, placeholder, type = "text", step, testId, required }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-1.5">{label}</div>
      <input
        type={type}
        step={step}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 rounded-md border border-navy/15 focus:border-emerald-500 outline-none text-ink text-sm"
        data-testid={testId}
      />
    </div>
  );
}
